import type { ProductImageEntry } from "./types";

/** Native visit and shift details backed by saved S1 assignments and shifts. */
export const VISIT_EVIDENCE_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  {
    id: "planning.visit.layer-windows",
    module: "Planering · Besökets tidsfönster",
    area: "schedule",
    opens: "Kalender → vald S1-lösning → Besöksdetaljer → Tid",
    setup: [
      "Verifiera den exakta sparade S1-lösningen och hitta ett tilldelat besök vars länkade mall har både krav och önskat tidsfönster",
      "Öppna kalendern för samma lösning och välj besöket från dess verkliga tilldelning",
      "Visa besöksdetaljerna när mallens sparade tidsfönster har laddats",
    ],
    scenario: "S1",
    visual: { variant: 5, focus: "time-windows" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Besöksdetaljer med önskat och obligatoriskt tidsfönster från den länkade genomförandeplanen",
        shows:
          "Den inbyggda besöksvyn visar de sparade önskade och obligatoriska tidsfönstren från besökets länkade mall.",
        proves:
          "Planeraren kan granska båda tidsfönstren för ett verkligt tilldelat besök. Bilden visar mallens sparade fönster, inte besökets schemalagda starttid eller ett ändrat planeringsvärde.",
      },
      en: {
        alt: "Visit details with preferred and required time windows from the linked care plan",
        shows:
          "The native visit view shows the saved preferred and required time windows from the visit's linked template.",
        proves:
          "The planner can inspect both time windows for a real assigned visit. The image shows the template's saved windows, not the scheduled start time or an edited planning value.",
      },
    },
  },
  {
    id: "planning.visit.travel-detail",
    module: "Planering · Besökets restid",
    area: "schedule",
    opens: "Kalender → vald S1-lösning → Besöksdetaljer → Restid",
    setup: [
      "Verifiera den exakta sparade S1-lösningen och välj ett tilldelat besök med positiva sparade restidsvärden före och efter",
      "Öppna kalendern för samma lösning och välj samma tilldelade besök",
      "Visa de två restidsraderna i besöksdetaljerna utan att ändra eller härleda värden",
    ],
    scenario: "S1",
    visual: { variant: 5, focus: "travel-detail" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Besöksdetaljer med sparad restid före och efter ett tilldelat besök",
        shows:
          "Den inbyggda besöksvyn visar lösningens restidsvärden före och efter den valda tilldelningen.",
        proves:
          "Planeraren kan läsa den sparade restiden kring ett verkligt tilldelat besök. Värdena är restid för denna lösning och visar inte en ny ruttberäkning.",
      },
      en: {
        alt: "Visit details with saved travel time before and after an assigned visit",
        shows:
          "The native visit view shows the solution's travel-time values before and after the selected assignment.",
        proves:
          "The planner can read saved travel time around a real assigned visit. These values belong to this solution and do not show a new route calculation.",
      },
    },
  },
  {
    id: "planning.employee.break-window-detail",
    module: "Planering · Medarbetarens rastfönster",
    area: "schedule",
    opens: "Kalender → medarbetarrad → Visa anställd → Skift i schemat",
    setup: [
      "Verifiera S1:s sparade kalender och dess angivna skiftpool",
      "Läs skiften igen med samma schema och skiftpool som kalendern och välj en medarbetare med ett skift som har rastminuter och ett sparat rastfönster",
      "Öppna Visa anställd från samma medarbetarrad och visa det matchande skiftets rastfönster",
    ],
    scenario: "S1",
    visual: { variant: 5, focus: "break-window-detail" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Medarbetarens skiftdetaljer med ett sparat rastfönster",
        shows:
          "Den inbyggda medarbetarvyn visar rastens tillåtna fönster, begränsat till det aktuella skiftet, från samma sparade skiftpool som kalendern.",
        proves:
          "Planeraren kan läsa rastfönstret för ett verkligt sparat skift. Fönstret bygger på den sparade rastposten och begränsas av skiftets start och slut; det är inte ett syntetiskt standardfönster.",
      },
      en: {
        alt: "Employee shift details with a saved break window",
        shows:
          "The native employee view shows the allowed break window, bounded by the selected shift, from the same saved shift pool as the calendar.",
        proves:
          "The planner can inspect a break window for a real saved shift. The window uses the persisted break row and is bounded by the shift's start and end; it is not a synthetic default window.",
      },
    },
  },
];
